/* eslint-disable react-refresh/only-export-components */
import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { useI18n } from '@/hooks/use-i18n';

// Functional component wrapper to use hooks
function ErrorBoundaryContent({
  error,
  retryCount,
  onReload,
  onRetry,
}: {
  error: Error;
  retryCount: number;
  onReload: () => void;
  onRetry: () => void;
}) {
  const { t } = useI18n();

  return (
    <div className='flex min-h-screen items-center justify-center p-4'>
      <div className='max-w-md w-full space-y-4 text-center'>
        <div className='flex justify-center'>
          <AlertCircle className='h-16 w-16 text-destructive' />
        </div>

        <h2 className='text-2xl font-bold'>
          {t('errors.chunkLoad.updateRequired')}
        </h2>

        <p className='text-muted-foreground'>
          {t('errors.chunkLoad.updateDescription')}
        </p>

        <div className='space-y-2'>
          <Button onClick={onReload} className='w-full' size='lg'>
            <RefreshCw className='mr-2 h-4 w-4' />
            {t('errors.chunkLoad.refreshApplication')}
          </Button>

          {retryCount < 3 && (
            <Button onClick={onRetry} variant='outline' className='w-full'>
              {t('errors.chunkLoad.retryButton')}
            </Button>
          )}
        </div>

        {import.meta.env.DEV && (
          <details className='mt-4 text-left'>
            <summary className='cursor-pointer text-sm text-muted-foreground'>
              {t('errors.chunkLoad.errorDetails')}
            </summary>
            <pre className='mt-2 text-xs bg-muted p-2 rounded overflow-auto'>
              {error.stack}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  retryCount: number;
}

export class DynamicImportErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Check if it's a dynamic import error
    const isDynamicImportError =
      error.message
        .toLowerCase()
        .includes('failed to fetch dynamically imported module') ||
      error.message.toLowerCase().includes('failed to import') ||
      error.message.toLowerCase().includes('loading chunk') ||
      error.message.toLowerCase().includes('loading css chunk') ||
      error.message
        .toLowerCase()
        .includes('is not a valid javascript mime type') ||
      error.message.toLowerCase().includes('unexpected application error');

    if (isDynamicImportError) {
      return {
        hasError: true,
        error,
        retryCount: 0,
      };
    }

    // Re-throw non-dynamic import errors
    throw error;
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Dynamic import error caught:', error, errorInfo);

    // Log to monitoring service if available
    if (typeof window !== 'undefined' && 'gtag' in window) {
      const gtag = window.gtag as (
        command: string,
        type: string,
        params: Record<string, unknown>
      ) => void;
      gtag('event', 'exception', {
        description: error.message,
        fatal: false,
      });
    }
  }

  handleReload = async () => {
    try {
      // Clear ALL caches to ensure fresh assets
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((name) => caches.delete(name)));
      }

      // Unregister service worker to force fresh registration
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(
          registrations.map((registration) => registration.unregister())
        );
      }

      // Small delay to ensure cleanup completes
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Force reload with cache bypass
      window.location.reload();
    } catch (error) {
      console.error('Error during cache cleanup:', error);
      // Fallback to simple reload
      window.location.reload();
    }
  };

  handleRetry = () => {
    this.setState((prevState) => ({
      hasError: false,
      error: null,
      retryCount: prevState.retryCount + 1,
    }));
  };

  render() {
    if (this.state.hasError && this.state.error) {
      // Use the functional component with hooks for i18n
      return (
        <ErrorBoundaryContent
          error={this.state.error}
          retryCount={this.state.retryCount}
          onReload={this.handleReload}
          onRetry={this.handleRetry}
        />
      );
    }

    return this.props.children;
  }
}
